import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/app/AppLayout";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Sun, Moon, Monitor, Download, ShieldAlert, Cookie, Check } from "lucide-react";
import ChangePasswordSection from "../components/settings/ChangePasswordSection";
import TwoFactorSection from "../components/settings/TwoFactorSection";
import WorkshopProfileSection from "../components/settings/WorkshopProfileSection";
import QuotingRulesSection from "../components/settings/QuotingRulesSection";
import DeleteAccountSection from "../components/settings/DeleteAccountSection";

export default function SettingsPage() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [exportOk, setExportOk] = useState(false);

  const exportData = async () => {
    setExporting(true); setError(""); setExportOk(false);
    try {
      const { data } = await api.get("/me/export-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `machinepilot-data-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setExportOk(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore esportazione");
    }
    setExporting(false);
  };

  const reopenCookieBanner = () => window.dispatchEvent(new Event("open-cookie-prefs"));

  const isAdmin = user?.role === "admin";

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-4xl mx-auto" data-testid="settings-page">
        <div className="mb-8">
          <div className="label-tech mb-2">Account</div>
          <h1 className="font-display font-bold text-3xl text-slate-100">{t("settings.title")}</h1>
          <p className="text-sm text-slate-400 mt-1">{t("settings.subtitle")}</p>
        </div>

        {/* Workshop profile */}
        <Section title={t("settings.profileTitle")} subtitle={t("settings.profileSub")}>
          <WorkshopProfileSection />
        </Section>

        {/* Quoting rules */}
        <Section title={t("settings.quotingRulesTitle")} subtitle={t("settings.quotingRulesSub")}>
          <QuotingRulesSection />
        </Section>

        {/* Theme */}
        <Section title={t("settings.theme")} subtitle={t("settings.themeSub")}>
          <div className="grid grid-cols-3 gap-3">
            <ThemeCard active={theme === "light"} onClick={() => setTheme("light")} icon={<Sun size={20}/>} label={t("settings.themeLight")} testid="theme-light"/>
            <ThemeCard active={theme === "dark"} onClick={() => setTheme("dark")} icon={<Moon size={20}/>} label={t("settings.themeDark")} testid="theme-dark"/>
            <ThemeCard active={theme === "system"} onClick={() => setTheme("system")} icon={<Monitor size={20}/>} label={t("settings.themeSystem")} testid="theme-system"/>
          </div>
        </Section>

        {/* Cookie preferences */}
        <Section title={t("settings.cookiePrefs")} subtitle={t("settings.cookiePrefsSub")}>
          <button
            onClick={reopenCookieBanner}
            className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm border border-slate-700 flex items-center gap-2"
            data-testid="reopen-cookie-prefs"
          >
            <Cookie size={14}/> {t("settings.editCookies")}
          </button>
          <p className="text-xs text-slate-500 mt-2">
            {t("settings.cookiePrefsHint", "Riapre il pannello di consenso cookie. Le tue scelte vengono salvate nel browser e sono valide per 6 mesi.")}
          </p>
        </Section>

        {/* Password */}
        <Section title={t("settings.passwordTitle")} subtitle={t("settings.passwordSub")}>
          <ChangePasswordSection />
        </Section>

        {/* Two-factor authentication */}
        <Section title={t("settings.twofaTitle")} subtitle={t("settings.twofaSub")}>
          <TwoFactorSection />
        </Section>


        {/* GDPR: data export */}
        <Section title={t("settings.myData")} subtitle={t("settings.myDataSub")}>
          <button
            onClick={exportData}
            disabled={exporting}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="export-data-btn"
          >
            <Download size={14}/> {exporting ? t("common.loading") : t("settings.exportData")}
          </button>
          {exportOk && (
            <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1" data-testid="export-success">
              <Check size={12}/> {t("settings.exportSuccess")}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-2">
            {t("settings.exportHint", "Scarica un file JSON con tutti i tuoi dati: account, preventivi, clienti, macchine, RFQ, offerte, notifiche, transazioni e conversazioni AI.")}
          </p>
        </Section>

        {/* GDPR: account deletion (with optional anonymous feedback) */}
        {!isAdmin && (
          <Section title={t("settings.deleteAccount")} subtitle={t("settings.deleteAccountSub")} danger>
            <DeleteAccountSection />
          </Section>
        )}

        {isAdmin && (
          <Section title="Admin" subtitle="">
            <div className="text-sm text-amber-300 flex items-center gap-2">
              <ShieldAlert size={16}/> {t("settings.adminLocked")}
            </div>
          </Section>
        )}

        {error && <div className="mt-4 p-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="settings-error">{error}</div>}

        <div className="mt-8 text-xs text-slate-500 space-x-3">
          <a href="/privacy" className="hover:text-amber-400">{t("legal.privacy")}</a>
          <a href="/cookies" className="hover:text-amber-400">{t("legal.cookies")}</a>
          <a href="/terms" className="hover:text-amber-400">{t("legal.terms")}</a>
        </div>
      </div>
    </AppLayout>
  );
}

function Section({ title, subtitle, children, danger }) {
  return (
    <section className={`mb-6 p-5 rounded-lg border ${danger ? "border-red-500/40 bg-red-500/5" : "border-slate-800 bg-slate-900/40"}`}>
      <div className="mb-3">
        <h2 className="font-display font-semibold text-slate-100 text-lg">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function ThemeCard({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-md border-2 transition-all flex flex-col items-center gap-2 ${active ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-slate-700 hover:border-slate-500 text-slate-300"}`}
      data-testid={testid}
    >
      {icon}
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}
